import * as React from 'react';
import { Grid, GridCellProps, GridColumn as Column, GridFilterOperators, GridPageChangeEvent, GridToolbar } from '@progress/kendo-react-grid';
import { Input } from "@progress/kendo-react-inputs";
import { process } from "@progress/kendo-data-query";
import './UserManagement.css';

const filterOperators: GridFilterOperators = {
    text: [{ text: 'grid.filterContainsOperator', operator: 'contains' }],
    numeric: [{ text: 'grid.filterEqOperator', operator: 'eq' }],
    date: [{ text: 'grid.filterEqOperator', operator: 'eq' }],
    boolean: [{ text: 'grid.filterEqOperator', operator: 'eq' }],
};

interface UserListProps {
    editCell: React.ComponentType<GridCellProps>;
    enableCell: React.ComponentType<GridCellProps>;
    records: any[];
    companyName: string;
}

interface PageState {
    filterValue: string;
    filteredRecords: any[];
    dataResult: any;
    dataState: {
        skip: number;
        take: number;
    }
    unfilteredRecords: any[];
    companyName: string;
}

class UserList extends React.Component<UserListProps, PageState> {
    public constructor(props: UserListProps) {
        super(props);
        this.state = {
            filterValue: '',
            filteredRecords: this.props.records,
            dataState: {
                skip: 0,
                take: 10,
            },
            dataResult: process(this.props.records, { skip: 0, take: 10 }),
            unfilteredRecords: this.props.records,
            companyName: this.props.companyName
        };
    }

    componentDidUpdate(prevProps: UserListProps, prevState: PageState) {
        const companyChanged = prevState.companyName !== this.props.companyName;
        const recordsChanged = prevProps.records !== this.props.records;

        if (recordsChanged) {
            const { skip, take } = this.state.dataState;
            const { filterValue } = this.state;

            const filtered = this.props.records.filter((item) =>
                item["email"]
                    .toString()
                    .toLocaleLowerCase()
                    .includes(filterValue.toLocaleLowerCase())
            );

            const total = filtered.length;

            // Only reset skip if company changed
            let adjustedSkip = companyChanged ? 0 : skip;

            // Ensure skip is valid
            if (adjustedSkip >= total && total !== 0) {
                adjustedSkip = Math.floor((total - 1) / take) * take;
            }

            const newDataState = { skip: adjustedSkip, take };
            const processed = process(filtered, newDataState);

            this.setState({
                unfilteredRecords: this.props.records,
                filteredRecords: filtered,
                dataResult: processed,
                dataState: newDataState,
                companyName: this.props.companyName
            });
        }
    }

    getHighlight = (value: any, filter: any) => {
        const { filterValue } = this.state;
        const index = value
            .toLocaleLowerCase()
            .indexOf(filterValue.toLocaleLowerCase());

        if (index >= 0) {
            const left = value.substr(0, index);
            const right = value.substring(index + filter.length, value.length);

            return (
                <React.Fragment>
                    {left}
                    <span className="highligth">
                        {value.substr(index, filter.length)}
                    </span>
                    {this.getHighlight(right, filter)}
                </React.Fragment>
            );
        }

        return value;
    };

    onFilterChange = (ev: any) => {
        const { unfilteredRecords, dataState } = this.state;
        const value = ev.value;
        const newData = unfilteredRecords.filter((item) => {
            let match = false;
            if (
                item["email"]
                    .toString()
                    .toLocaleLowerCase()
                    .indexOf(value.toLocaleLowerCase()) >= 0
            ) {
                match = true;
            }
            if (
                item["email"].toLocaleDateString &&
                item["email"].toLocaleDateString().indexOf(value) >= 0
            ) {
                match = true;
            }
            return match;
        });
        const clearedPagerDataState = { ...dataState, take: 10, skip: 0 };
        const processedData = process(newData, clearedPagerDataState);

        this.setState({
            filterValue: value,
            filteredRecords: newData,
            dataResult: processedData,
            dataState: clearedPagerDataState,
        });
    };

    dataStateChange = (event: any) => {
        const { filteredRecords } = this.state;
        const processedData = process(filteredRecords, event.dataState);

        this.setState({
            dataState: event.dataState,
            dataResult: processedData,
        });
    };

    expandChange = (event: any) => {
        const { dataResult } = this.state;
        const isExpanded =
            event.dataItem.expanded === undefined
                ? event.dataItem.aggregates
                : event.dataItem.expanded;

        event.dataItem.expanded = !isExpanded;

        this.setState({
            dataResult: { ...dataResult },
        });
    };

    cellRender = (td: any, props: any) => {
        const { filterValue } = this.state;
        const value = td.props.children;

        if (
            filterValue &&
            filterValue.length > 0 &&
            value.substr &&
            value.toLocaleLowerCase().indexOf(filterValue.toLocaleLowerCase()) >= 0
        ) {
            const children = this.getHighlight(
                value,
                filterValue.toLocaleLowerCase()
            );
            return React.cloneElement(td, [props], [children]);
        }

        return td;
    };

    handleDelete = (idToDelete: string) => {
        const { unfilteredRecords, dataState, filterValue } = this.state;

        const newUnfiltered = unfilteredRecords.filter(item => item.id !== idToDelete);
        const newFiltered = newUnfiltered.filter((item) => {
            if (
                item["email"]
                    .toString()
                    .toLocaleLowerCase()
                    .indexOf(filterValue.toLocaleLowerCase()) >= 0
            ) {
                return true;
            }
            return false;
        });

        this.setState(
            {
                unfilteredRecords: newUnfiltered,
                filteredRecords: newFiltered
            },
            () => {
                // Re-process grid state
                this.dataStateChange({ dataState });
            }
        );
    };

    public render() {
        return (
            <>
                <div style={{ paddingRight: "25px", width: "100%" }}>
                    <Grid
                        cellRender={this.cellRender}
                        style={{ fontSize: "12px" }}
                        resizable={true}
                        data={this.state.dataResult}
                        {...this.state.dataState}
                        onDataStateChange={this.dataStateChange}
                        expandField="expanded"
                        pageable={{
                            buttonCount: 4,
                            pageSizes: [10, 25, 50, 100],
                        }}
                        onExpandChange={this.expandChange}
                        skip={this.state.dataState.skip}
                        take={this.state.dataState.take}
                        total={this.state.dataResult.length}
                        scrollable={'none'}
                    >
                        <GridToolbar>
                            <div>
                                <span
                                    style={{
                                        padding: "5px",
                                    }}
                                >
                                    Search:{" "}
                                </span>
                                <span>
                                    <Input
                                        value={this.state.filterValue}
                                        onChange={this.onFilterChange}
                                        style={{
                                            border: "2px solid #ccc",
                                            boxShadow: "inset 0px 0px 0.5px 0px rgba(0,0,0,0.0.1)",
                                        }}
                                    />
                                </span>
                            </div>
                        </GridToolbar>
                        <Column field="id" title="ID" />
                        <Column field="username" title="Username" />
                        <Column field="enabled" title="Enabled" cell={this.props.enableCell}/>
                        <Column cell={this.props.editCell} />
                    </Grid>
                </div>
            </>
        );
    }
}

export default UserList;
